import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { DotsThreeVertical, X, type Icon } from "@phosphor-icons/react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import styles from "./ActionMenu.module.css";

export type Action = {
  key: string;
  icon: Icon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "danger";
  hint?: string;
};

type Props = {
  triggerLabel: string;
  title?: string;
  actions: Action[];
  closeLabel?: string;
  /** Optionaler Inhalt über der Action-Liste (z. B. inline-editierbare Felder). */
  content?: ReactNode;
};

export function ActionMenu({ triggerLabel, title, actions, closeLabel, content }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const menuId = useId();
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLButtonElement>(
      '[role="menuitem"]:not([disabled])',
    );
    first?.focus();
  }, [open]);

  useEffect(() => {
    if (!open && triggerRef.current && document.activeElement === document.body) {
      triggerRef.current.focus();
    }
  }, [open]);

  // Outside click handling for desktop popover
  useEffect(() => {
    if (!open || isMobile) return;
    const onDown = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open, isMobile]);

  const handleMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
    // Wenn Focus in Input/Textarea (inline-edit), nicht in Menu-Navigation eingreifen.
    const active = document.activeElement as HTMLElement | null;
    if (active?.matches?.("input, textarea")) return;
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [],
    );
    if (items.length === 0) return;
    e.preventDefault();
    const idx = items.indexOf(active as HTMLButtonElement);
    let next = 0;
    if (e.key === "ArrowDown") next = idx < 0 ? 0 : (idx + 1) % items.length;
    if (e.key === "ArrowUp") next = idx <= 0 ? items.length - 1 : idx - 1;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = items.length - 1;
    items[next]?.focus();
  };

  const handleBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  const runAction = (action: Action) => {
    setOpen(false);
    action.onClick();
  };

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <DotsThreeVertical size={20} weight="bold" />
      </button>

      {open && isMobile && (
        <div className={styles.backdrop} onMouseDown={handleBackdrop} role="presentation">
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={title ?? triggerLabel}
            aria-labelledby={title ? titleId : undefined}
            className={styles.sheet}
            onKeyDown={handleMenuKey}
          >
            <div className={styles.sheetHeader}>
              {title && (
                <h3 id={titleId} className={styles.sheetTitle}>
                  {title}
                </h3>
              )}
              <button
                type="button"
                className={styles.sheetClose}
                aria-label={closeLabel ?? "Close"}
                onClick={() => setOpen(false)}
              >
                <X size={20} weight="regular" />
              </button>
            </div>
            {content && <div className={styles.contentZone}>{content}</div>}
            <ActionList actions={actions} onActivate={runAction} variant="sheet" />
          </div>
        </div>
      )}

      {open && !isMobile && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={title ?? triggerLabel}
          className={styles.popover}
          onKeyDown={handleMenuKey}
        >
          {content && <div className={styles.contentZone}>{content}</div>}
          <ActionList actions={actions} onActivate={runAction} variant="popover" />
        </div>
      )}
    </div>
  );
}

function ActionList({
  actions,
  onActivate,
  variant,
}: {
  actions: Action[];
  onActivate: (a: Action) => void;
  variant: "sheet" | "popover";
}) {
  return (
    <ul className={`${styles.list} ${variant === "sheet" ? styles.listSheet : styles.listPopover}`}>
      {actions.map((action) => (
        <li key={action.key}>
          <button
            type="button"
            role="menuitem"
            className={`${styles.item} ${
              action.variant === "danger" ? styles.itemDanger : ""
            }`}
            disabled={action.disabled}
            onClick={() => onActivate(action)}
          >
            <action.icon size={20} weight="regular" />
            <span className={styles.itemLabel}>{action.label}</span>
            {action.hint && <span className={styles.itemHint}>{action.hint}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}
